import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, of, switchMap, throwError } from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
import jwt_decode, { jwtDecode } from 'jwt-decode'; // Import using the named export
import { JwtDecodeOptions, JwtHeader, JwtPayload } from 'jwt-decode';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';


@Injectable()
export class AuthService {
    private _authenticated: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post(`${environment.apis.lineupUrl}auth/v1/forgot-password`, { email });
    }

    /**
     * Reset password
     *
     * @param email
     * @param token
     * @param newPassword
     */
    resetPassword(email: string, token: string, newPassword: string): Observable<any> {
        return this._httpClient.post(`${environment.apis.lineupUrl}auth/v1/reset-password`, { email, token, newPassword });
    }

    getPermissions() {
        return this._httpClient.get(`${environment.apis.lineupUrl}auth/v1/permissions`);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any> {
       

        return this._httpClient.post(`${environment.apis.lineupUrl}auth/v1/sign-in`, credentials).pipe(
            switchMap((response: any) => {

                // Store the access token in the local storage
                this.accessToken = response.accessToken;

                // Decode the token to get user details
                const decodedToken = jwtDecode<JwtPayload & { id: string; email: string; name: string, role: string[], permissions: string[] }>(this.accessToken);

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = { id: decodedToken.id, name: decodedToken.name, email: decodedToken.email, avatar: '', role: decodedToken.role, permissions: decodedToken.permissions };
                 // Retrieve the device token from Firebase Messaging and store it in localStorage
                    return this.getDeviceToken().pipe(
                        switchMap((deviceToken) => {
                            // Now, call the backend with the device token
                            if (deviceToken) {
                                return this.registerTokenWithBackend(deviceToken);
                            }
                            return of(null);  // Return an observable with a null response if no token is found
                        }),
                        switchMap(() => of(response)) // Continue with the original sign-in response after registering the token
                    );
                    })
        );
    }
    
    // Método para obter o token do dispositivo
    getDeviceToken(): Observable<string | null> {
        const messaging = getMessaging();
        return new Observable<string | null>((observer) => {
            getToken(messaging, { vapidKey: environment.firebase.vapidKey })
                .then((currentToken) => {
                    if (currentToken) {
                        localStorage.setItem('device-token', currentToken);
                        observer.next(currentToken);
                    } else {
                        observer.next(null); // Caso não haja token
                    }
                    observer.complete();
                })
                .catch((err) => {
                    console.error('Erro ao recuperar o token do dispositivo:', err);
                    observer.next(null);
                    observer.complete();
                });
        });
    }
    

    registerTokenWithBackend(token: string): Observable<any> {
        const payload = {
        token: token,
        provider: 'Firebase',
        name: 'Device'
        };
        
        return this._httpClient.post(`${environment.apis.lineupUrl}auth/v1/register-token-provider`, payload);
    }

    refreshPage() {

        if (this.accessToken) {
            const decodedToken = jwtDecode<JwtPayload & { id: string; email: string; name: string, role: string[], permissions: string[] }>(this.accessToken);
            // Store the user on the user service
            this._userService.user = { id: decodedToken.id, name: decodedToken.name, email: decodedToken.email, avatar: '', role: decodedToken.role, permissions: decodedToken.permissions };
        }
    }
    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {

        // Sign in using the token
        return this._httpClient.post(`${environment.apis.lineupUrl}auth/v1/sign-in-with-token`, {}).pipe(
            catchError(() =>

                // Return false
                of(false)
            ),
            switchMap((response: any) => {

                // Replace the access token with the new one if it's available on
                // the response object.
                if (response.accessToken) {
                    this.accessToken = response.accessToken;
                }

                // Set the authenticated flag to true
                this._authenticated = true;

                const decodedToken = jwtDecode<JwtPayload & { id: string; email: string; name: string, role: string[], permissions: string[] }>(this.accessToken);

                // Store the user on the user service
                this._userService.user = { id: decodedToken.id, name: decodedToken.name, email: decodedToken.email, avatar: '', role: decodedToken.role, permissions: decodedToken.permissions };

                // Return true
                return of(true);
            })
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
        return this._httpClient.post(`${environment.apis.lineupUrl}auth/v1/sign-up`, user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post(`${environment.apis.lineupUrl}auth/v1/unlock-session`, credentials);
    }

    hasPermission(permission: string): Observable<boolean> {
        return this._userService.user$.pipe(
          map(user => {
            return !user?.permissions?.includes(permission);
          })
        );
      }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists and it didn't expire, sign in using it
        return of(true);
    }

    /**
     * Confirm email
     *
     * @param email
     * @param token
     */
     confirmEmail(email: string, token: string): Observable<any> {
        const params = new HttpParams().set('email', email).set('token', token);
        return this._httpClient.get(`${environment.apis.lineupUrl}auth/v1/confirm-email`, { params }).pipe(
            catchError((error) => {
                return throwError(error);
            })
        );
    }

}
